import React, { useCallback, useEffect, useMemo, useState } from "react";
import { leaveBalanceTitle } from "../../Constant/title";
import { AlertCircle } from "react-feather";
import { notFound, Search, Submit } from "../../Constant";
import * as yup from "yup";
import {
  checkStatusCodeSuccess,
  errHandler,
  extractDate,
  getFinancialYear,
  getItem,
  getLocalItem,
  handleResponse,
  isUserProfile,
  moduleName,
} from "../../Constant/common";
import { useNavigate, useParams } from "react-router-dom";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { toast } from "react-toastify";
import {
  editLeaveBalanceApi,
  leaveBalanceHistoryApi,
  staffLeaveBalanceApi,
} from "../../Api/leaveBalanceApi";
import { leaveBalanceEnums } from "../../Constant/HR/leaveBalance";
import { useFormik } from "formik";
import BaseButton from "../../BaseComponents/BaseButton";
import BackButton from "../../BaseComponents/BackButton";
import { PlaceHolderFormat } from "../../Constant/requireMessage";
import BaseInput from "../../BaseComponents/BaseInput";
import { HR } from "../../Constant/hrLayout";
import TableContainer from "../../BaseComponents/BaseTable";
import { fractionNumberRegex, validationMessages } from "../../Constant/validation";

const MAX_LEAVE_COUNT_INPUT = 10;
const LeaveBalance = () => {
  const [btnLoader, setBtnLoader] = useState(false);
  const { setLoading } = usePageLoader();
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [searchLeaveHistory, setSearchLeaveHistory] = useState([]);
  const [initialLeaves, setInitialLeaves] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [customPageSize, setCustomPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const { staffId } = useParams();
  const currentDate = new Date();
  const financialYear = getFinancialYear(currentDate);
  const isProfile = JSON.parse(getItem(isUserProfile) || false);
  const profileId = getLocalItem("id");
  const finalId = isProfile ? profileId : staffId;
  const navigate = useNavigate();
  const permission = JSON.parse(getLocalItem("permissions"));
  const hasLeaveBalance = permission?.some(
    (p) => p.parent_module_name === HR && p.module_name === moduleName.LeaveBalance,
  );

  useEffect(() => {
    document.title = leaveBalanceTitle;
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object({
      leaves: yup.array().of(
        yup.object({
          leave_count: yup
            .string()
            .required(validationMessages.required(leaveBalanceEnums.leaveCount))
            .matches(fractionNumberRegex, validationMessages.format(leaveBalanceEnums.leaveCount)),
        }),
      ),
    }),
    initialValues: {
      leaves:
        initialLeaves?.length > 0
          ? initialLeaves
          : [
              {
                leave_template_id: null,
                leave_category_id: null,
                leave_category_name: null,
                remaining_leave: null,
                leave_count: null,
                total_leave: null,
              },
            ],
    },
    onSubmit: (values) => {
      const payload = {
        leaves: values.leaves
          ?.filter((item) => item?.leave_count && Number(item?.leave_count) !== 0)
          .map((item) => ({
            leave_template_id: Number(item?.leave_template_id),
            leave_category_id: Number(item?.leave_category_id),
            leave_count: Number(item?.leave_count || 0),
          })),
      };
      handleSubmit(payload);
    },
  });

  const fetchLeaveBalanceHistory = useCallback(() => {
    setLoading(true);
    leaveBalanceHistoryApi({
      staffId: Number(finalId),
      order: [columnName, sortOrder],
      pageNumber: currentPage,
      pageSize: customPageSize,
    })
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setLeaveHistory(res?.data?.leaveBalanceHistory);
          setTotalPages(res?.data?.totalPages);
          setTotalRecords(res?.data?.totalRecordsCount);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          setCurrentPage(res?.data?.currentPage);
        } else setLeaveHistory([]);
      })
      .catch(() => setLeaveHistory([]))
      .finally(() => setLoading(false));
  }, [finalId, columnName, sortOrder, currentPage, customPageSize, setLoading]);

  const fetchSearchHistoryData = useCallback(() => {
    setLoading(true);
    leaveBalanceHistoryApi({
      staffId: Number(finalId),
      order: [columnName, sortOrder],
      search: searchValue,
      pageNumber: currentPage,
      pageSize: customPageSize,
    })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setSearchLeaveHistory(resp?.data?.leaveBalanceHistory);
          setTotalRecords(resp.data.totalRecordsCount);
          setTotalPages(resp?.data?.totalPages);
          setTotalNumberOfRows(resp?.data?.numberOfRows);
          setCurrentPage(resp?.data?.currentPage);
        } else setSearchLeaveHistory([]);
      })
      .catch(() => setSearchLeaveHistory([]))
      .finally(() => setLoading(false));
  }, [finalId, columnName, sortOrder, searchValue, currentPage, customPageSize, setLoading]);

  const fetchLeaveData = useCallback(() => {
    setLoading(true);
    staffLeaveBalanceApi(finalId, {})
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const leaves = res?.data?.filter(
            (item) => item?.leave_category_name !== leaveBalanceEnums.unpaidLeave,
          );
          setInitialLeaves(
            leaves?.map((item) => ({
              leave_template_id: item?.leave_id,
              leave_category_id: item?.leave_category_id,
              leave_category_name: item?.leave_category_name,
              remaining_leave: item?.remaining_leave,
              leave_count: null,
              total_leave: null,
            })) || [],
          );
          fetchLeaveBalanceHistory();
        }
      })
      .catch((err) => err)
      .finally(() => setLoading(false));
  }, [finalId, fetchLeaveBalanceHistory, setLoading]);

  useEffect(() => {
    const t = setTimeout(fetchLeaveData, 0);
    return () => clearTimeout(t);
  }, [fetchLeaveData]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchValue) fetchSearchHistoryData();
      else fetchLeaveBalanceHistory();
    }, 0);
    return () => clearTimeout(t);
  }, [searchValue, fetchSearchHistoryData, fetchLeaveBalanceHistory]);

  const handleSearchValueChange = (value) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };
  const handleFetchData = (page) => setCurrentPage(page);
  const handleFetchSorting = (page, id, order) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const historyColumn = useMemo(
    () => [
      {
        header: leaveBalanceEnums.SrNo,
        accessorKey: "serial_number",
        cell: (cell) => cell.row.index + 1,
        enableColumnFilter: false,
      },
      {
        header: leaveBalanceEnums.leaveCategory,
        accessorKey: "leave_category_name",
        enableColumnFilter: false,
      },
      {
        header: leaveBalanceEnums.previousCount,
        accessorKey: "remaining_leave_count",
        enableColumnFilter: false,
      },
      {
        header: leaveBalanceEnums.leaveCount,
        accessorKey: "leave_count",
        enableColumnFilter: false,
      },
      {
        header: leaveBalanceEnums.finalCount,
        accessorKey: "total_leave",
        enableColumnFilter: false,
      },
      { header: leaveBalanceEnums.addedBy, accessorKey: "addedBy.name", enableColumnFilter: false },
      {
        header: leaveBalanceEnums.updatedOn,
        accessorKey: "date",
        enableColumnFilter: false,
        cell: (cell) => extractDate(cell.row.original?.date) ?? handleResponse.nullData,
      },
    ],
    [],
  );

  const totalRemainingLeaves = useMemo(
    () => formik.values.leaves?.reduce((sum, l) => sum + (Number(l?.remaining_leave) || 0), 0) ?? 0,
    [formik.values.leaves],
  );

  const isAnythingAdded = useMemo(
    () => formik.values.leaves?.some((l) => l?.leave_count && Number(l?.leave_count) !== 0),
    [formik.values.leaves],
  );

  const handleSubmit = (payload) => {
    setBtnLoader(true);
    editLeaveBalanceApi(finalId, payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          toast.success(res?.message);
          fetchLeaveData();
        } else toast.error(res?.message);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message;
        const leavesErrors = [];
        let hasCountErrors = false;

        const parseError = (errorMsg) => {
          if (typeof errorMsg === "string" && errorMsg.includes("leave_count")) {
            const match = errorMsg.match(/leaves\[(\d+)\]\.leave_count/);
            if (match) {
              const index = parseInt(match[1], 10);
              if (!leavesErrors[index]) {
                leavesErrors[index] = {};
              }
              leavesErrors[index].leave_count = "Leave count is required.";
              hasCountErrors = true;
            }
          }
        };

        if (typeof msg === "string") {
          parseError(msg);
        } else if (Array.isArray(msg)) {
          msg.forEach((m) => parseError(m));
        }

        if (hasCountErrors) {
          formik.setErrors({ leaves: leavesErrors });
          const touchedLeaves = formik.values.leaves.map((_, idx) => {
            if (leavesErrors[idx]) {
              return { leave_count: true };
            }
            return {};
          });
          formik.setTouched({ leaves: touchedLeaves });
          toast.error(validationMessages.required(leaveBalanceEnums.leaveCount));
        } else {
          errHandler(err);
        }
      })
      .finally(() => setBtnLoader(false));
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "1.125rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            {leaveBalanceEnums.leaveBalance}
          </h2>
          <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)" }}>
            {financialYear}
          </p>
        </div>
        <BackButton
          onClick={() => (isProfile ? navigate("/profile") : navigate(`/staffProfile/${staffId}`))}
        />
      </div>

      {/* Balance stat */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 16,
          padding: "14px 20px",
          marginBottom: 20,
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 4,
            }}
          >
            {leaveBalanceEnums.balanceLeave}
          </div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#10b981" }}>
            {totalRemainingLeaves}
          </div>
        </div>
      </div>

      {/* Leave balance form */}
      <div
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          marginBottom: 20,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid var(--glass-border)",
            background: "rgba(99,102,241,0.03)",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "0.9375rem",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {leaveBalanceEnums.leaveBalance}
          </h3>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <div style={{ padding: "20px" }}>
            {formik.values.leaves?.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "40px 20px",
                  gap: 12,
                }}
              >
                <AlertCircle size={36} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
                <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {formik.values.leaves?.map((leave, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "14px 0",
                      borderBottom:
                        index < formik.values.leaves.length - 1
                          ? "1px solid var(--glass-border)"
                          : "none",
                    }}
                  >
                    <div className="row">
                      <div className="col-lg-3">
                        <BaseInput
                          type="text"
                          label={leaveBalanceEnums.leaveCategory}
                          name={`leaves.${index}.leave_category_name`}
                          placeholder={PlaceHolderFormat(leaveBalanceEnums.leaveCategory)}
                          value={leave.leave_category_name}
                          handleChange={formik.handleChange}
                          handleBlur={formik.handleBlur}
                          error={formik.errors?.leaves?.[index]?.leave_category_name}
                          touched={formik.touched?.leaves?.[index]?.leave_category_name}
                          disabled
                        />
                      </div>
                      <div className="col-lg-3">
                        <BaseInput
                          type="text"
                          label={leaveBalanceEnums.remainingLeave}
                          name={`leaves.${index}.remaining_leave`}
                          placeholder={PlaceHolderFormat(leaveBalanceEnums.remainingLeave)}
                          value={formik.values.leaves?.[index].remaining_leave}
                          handleChange={formik.handleChange}
                          handleBlur={formik.handleBlur}
                          error={formik.errors?.leaves?.[index]?.remaining_leave}
                          touched={formik.touched?.leaves?.[index]?.remaining_leave}
                          disabled
                        />
                      </div>
                      <div className="col-lg-3">
                        <BaseInput
                          type="text"
                          label={leaveBalanceEnums.leaveCount}
                          name={`leaves.${index}.leave_count`}
                          placeholder={PlaceHolderFormat(leaveBalanceEnums.leaveCount)}
                          value={formik.values.leaves?.[index].leave_count}
                          handleChange={(e) => {
                            if (e.target.value <= MAX_LEAVE_COUNT_INPUT) formik.handleChange(e);
                          }}
                          handleBlur={formik.handleBlur}
                          error={formik.errors?.leaves?.[index]?.leave_count}
                          touched={formik.touched?.leaves?.[index]?.leave_count}
                          disabled={!hasLeaveBalance}
                        />
                      </div>
                      <div className="col-lg-3">
                        <BaseInput
                          type="number"
                          label={leaveBalanceEnums.totalLeave}
                          name={`leaves.${index}.total_leave`}
                          placeholder={PlaceHolderFormat(leaveBalanceEnums.leaveCount)}
                          value={
                            Number(formik.values.leaves?.[index].leave_count || 0) +
                            Number(formik.values.leaves?.[index].remaining_leave || 0)
                          }
                          handleChange={formik.handleChange}
                          handleBlur={formik.handleBlur}
                          error={formik.errors?.leaves?.[index]?.total_leave}
                          touched={formik.touched?.leaves?.[index]?.total_leave}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {formik.values.leaves?.length > 0 && (
            <div
              style={{
                padding: "14px 20px",
                borderTop: "1px solid var(--glass-border)",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <BaseButton
                type="submit"
                color="primary"
                loader={btnLoader}
                disabled={btnLoader || !isAnythingAdded}
              >
                {Submit}
              </BaseButton>
            </div>
          )}
        </form>
      </div>

      {/* History table */}
      <div
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid var(--glass-border)",
            background: "rgba(99,102,241,0.03)",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "0.9375rem",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {leaveBalanceEnums.leaveBalanceHistory}
          </h3>
        </div>
        <div style={{ padding: "16px" }}>
          {leaveHistory?.length > 0 ? (
            <TableContainer
              totalPages={totalPages}
              totalRecords={totalRecords}
              totalNumberOfRows={totalNumberOfRows}
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              fetchData={handleFetchData}
              customPageSize={customPageSize}
              setCustomPageSize={setCustomPageSize}
              fetchSortingData={handleFetchSorting}
              columns={historyColumn}
              shouldLineBreak={false}
              data={searchValue ? searchLeaveHistory : leaveHistory || []}
              manualPagination={true}
              SearchPlaceholder={Search}
              onSearch={handleSearchValueChange}
              isGlobalFilter={true}
              manualFiltering={true}
            />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "40px 20px",
                gap: 12,
              }}
            >
              <AlertCircle size={36} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
              <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveBalance;
